import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getStyles } from './styles';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../Header/Header";
import batchService from "../../services/BatchService";
import { BatchErrorDTO, SiteID, SyncBatch, SyncBatchStatus, SyncBatchTransformation, UUIDv1 } from "@beable/roster-backend-client-v1";
import { Grid, CircularProgress, Button, Tooltip, Box } from "@mui/material";
import CustomerAndSiteSelector from "../Common/CustomerAndSiteSelector";
import authService from "../../services/AuthService";
import { CustomerDTO, SiteDTO, Roles } from "@beable/roster-backend-client-v1";
import RefreshIcon from "@mui/icons-material/Refresh";
import BasePanel from "../Common/Layout/BasePanel";
import { BasePanelBox } from "../Common/Layout/BasePanelBox";
import BaseSheet from "../Common/Layout/BaseSheet";
import { StyledPanelHeader } from "../Common/Layout/PanelHeader";
import BatchFileList from "./components/BatchFileList";

const dateOptions: Intl.DateTimeFormatOptions = {
  hour12: true,
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};


const initialBatchesValue: SyncBatch[] | undefined = undefined;

export default function BatchStatus() {
  const params =
    useParams<{
      customerId?: string | undefined;
      siteId?: string | undefined;
    }>();
  const admin = authService.getUser();

  const theme = useTheme();
  const styles = getStyles(theme);

  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(
    Number(params.siteId)
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(params.customerId);
  const [batches, setBatches] = useState(initialBatchesValue);
  const [transformations, setTransformations] = useState<SyncBatchTransformation[]>([]);
  const [batchErrors, setBatchErrors] = useState<BatchErrorDTO[]>([]);

  const handleSelectedCustomerChange = (customer: CustomerDTO) => {
    setSelectedCustomerId(customer.id);
    setBatches(initialBatchesValue);
  };

  const handleSelectedSiteChange = (site?: SiteDTO) => {
    if (site) {
      getLastThreeBatches(site.id);
      setSelectedSiteId(site.id);
    } else {
      setBatches(undefined);
      setTransformations([]);
      setBatchErrors([]);
      setSelectedSiteId(undefined);
    }
  };

  const getLastThreeBatches = async (siteId: number) => {
    setIsLoadingBatches(true);
    const batches = await batchService.getSyncBatches(siteId, 3);
    if (batches) {
      setBatches(batches);
      setBatchInfo({ siteId: siteId as SiteID, syncBatchIds: batches.map(b => b.id), legacyBatchIds: batches.map(b => b.legacyBatchId) });
      setIsLoadingBatches(false);
    } else {
      setIsLoadingBatches(false);
      window.alert("Could not get the batches");
    }
  };

  const handleActionComplete = (siteId: number) => {
    getLastThreeBatches(siteId);
  };

  const refreshBatches = () => {
    getLastThreeBatches(selectedSiteId!);
  };

  const setBatchInfo = async (params: { siteId: SiteID, syncBatchIds: UUIDv1[], legacyBatchIds: UUIDv1[] }) => {
    const result = await batchService.getBatchInfo(params);
    if (result) {
      setTransformations(result.transformations);
      setBatchErrors(result.errors);
    }
  }

  const getPreselectedSite = () => {
    if (params.siteId && selectedSiteId === Number(params.siteId)) {
      return selectedSiteId;
    }
    return undefined;
  };

  const getStatusStyle = (status: SyncBatchStatus) => {
    return {
      [SyncBatchStatus.RUNNING]: styles.running,
      [SyncBatchStatus.SUSPENDED]: styles.suspended,
      [SyncBatchStatus.APPROVED]: styles.approved,
      [SyncBatchStatus.FAILED]: styles.failed,
      [SyncBatchStatus.LOCK_TIMEOUT]: styles.failed,
      [SyncBatchStatus.CANCELED]: styles.cancelled,
      [SyncBatchStatus.COMPLETED]: styles.finished,
      [SyncBatchStatus.CREATED]: styles.created,
    }[status]
  }

  const getTriggeredByLabel = (batch: SyncBatch) => {
    if (batch.triggeredByUserAccountId != null) {
      return  batch.triggeredByUserAccountName ? "Educator " + batch.triggeredByUserAccountName : "Educator";
    }
    return "Support";
  };

  return (
    <>
      <Header />
      <Grid container>
        <BasePanel>
          <BasePanelBox>
            <StyledPanelHeader header="CSV Batch Status" />
            <Grid container size={12}>
              <CustomerAndSiteSelector
                onSiteSelect={handleSelectedSiteChange}
                onCustomerSelect={handleSelectedCustomerChange}
                preselectedCustomer={
                  params.customerId ? selectedCustomerId : undefined
                }
                preselectedSite={getPreselectedSite()}
              />
            </Grid>
          </BasePanelBox>
        </BasePanel>
        <BaseSheet maxWidth="lg">
          <Grid size={12}>
            {isLoadingBatches && (
              <Box sx={styles.loaderContainer}>
                <CircularProgress test-id="batch-loader" data-testid="batch-loader" disableShrink />
              </Box>
            )}
            {batches &&
              batches.length > 0 &&
              !isLoadingBatches ?
              <>
                Refresh to check for new batches
                <Button
                  onClick={refreshBatches}
                  sx={{ width: "30px !important", minWidth: "30px" }}
                >
                  <RefreshIcon />
                </Button>
                {batches.map((batchRecord, index) => {
                  const isEducatorTriggered = batchRecord.triggeredByUserAccountId != null;
                  return (
                    <Accordion test-id="batch-list" key={batchRecord.id} slotProps={{ heading: { component: 'div' } }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${batchRecord.id}-content`}
                        id={batchRecord.id}
                        key={`summary-${batchRecord.id}`}
                      >
                        <Typography sx={styles.batchNumber}>
                          {index + 1}
                        </Typography>
                        <Tooltip title={batchRecord.fileName} placement="top">
                          <Typography sx={styles.batchFileName}>
                            {batchRecord.fileName}
                          </Typography>
                        </Tooltip>
                        <Typography sx={styles.batchDate}>
                          Uploaded at:{" "}
                          {new Date(batchRecord.createdAt).toLocaleDateString(
                            "en-US",
                            dateOptions
                          )}
                        </Typography>
                        <Box sx={styles.batchTriggeredBy}>
                          <Typography sx={styles.triggeredByLabel}>
                            Triggered by
                          </Typography>
                          <Typography sx={[styles.triggeredByBadge, isEducatorTriggered ? styles.triggeredByTeacher : styles.triggeredByAdmin]}>
                            {getTriggeredByLabel(batchRecord)}
                          </Typography>
                        </Box>
                        <Typography sx={[styles.batchStatus, getStatusStyle(batchRecord.status)]}>
                          {batchRecord.status.toUpperCase()}
                        </Typography>
                      </AccordionSummary>
                      <BatchFileList
                        batch={batchRecord}
                        transformations={transformations.filter(t => t.syncBatchId === batchRecord.id)}
                        batchErrorDTOS={batchErrors.filter(e => e.legacyBatchId === batchRecord.legacyBatchId)}
                        onActionComplete={handleActionComplete}
                      />
                    </Accordion>
                  )
                })}</> : undefined
            }
            {batches &&
              batches.length === 0 &&
              !isLoadingBatches &&
              admin?.role === Roles.districtadmin &&
              "You haven't uploaded any batches yet..."}
            {batches &&
              batches.length === 0 &&
              !isLoadingBatches &&
              admin?.role === Roles.beableadmin &&
              (<>
                No batches found for this site
                <Button
                  onClick={refreshBatches}
                  sx={{ width: "30px !important", minWidth: "30px" }}
                >
                  <RefreshIcon />
                </Button>
              </>)
            }
          </Grid>
        </BaseSheet>
      </Grid>
    </>
  );
}
